import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, AppState } from '../types';
import authService from '../services/auth';
import { NotificationProvider } from './NotificationContext';

// 定义动作类型
type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_TOKEN'; payload: string | null }
  | { type: 'SET_AUTHENTICATED'; payload: boolean }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_COLLAPSED'; payload: boolean }
  | { type: 'LOGOUT' }
  | { type: 'INITIALIZE_STATE' };

// 初始状态
const initialState: AppState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  sidebarCollapsed: false,
};

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_TOKEN':
      return { ...state, token: action.payload };
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    case 'SET_SIDEBAR_COLLAPSED':
      return { ...state, sidebarCollapsed: action.payload };
    case 'LOGOUT':
      return {
        ...initialState,
        loading: false,
      };
    case 'INITIALIZE_STATE':
      const userStatus = authService.getUserStatus();
      return {
        ...state,
        user: userStatus.user,
        token: authService.getToken(),
        isAuthenticated: userStatus.isAuthenticated,
        loading: false,
      };
    default:
      return state;
  }
};

// 上下文类型定义
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  login: (user: User, token: string) => void;
  logout: () => Promise<void>;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  updateUser: (user: User) => void;
  checkAuthStatus: () => Promise<void>;
}

// 创建上下文
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider组件
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // 初始化应用状态
  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // 检查本地存储中的认证信息
      const userStatus = authService.getUserStatus();
      
      if (userStatus.isAuthenticated && userStatus.user) {
        // 如果用户需要初始化密码，直接使用本地缓存的信息，不调用API
        if (userStatus.needsPasswordInit) {
          dispatch({ type: 'INITIALIZE_STATE' });
        } else {
          // 尝试获取最新用户信息
          try {
            const currentUser = await authService.getCurrentUser();
            dispatch({ type: 'SET_USER', payload: currentUser });
            dispatch({ type: 'SET_TOKEN', payload: authService.getToken() });
            dispatch({ type: 'SET_AUTHENTICATED', payload: true });
          } catch (error) {
            // 如果获取用户信息失败，使用本地缓存的信息
            console.warn('Failed to fetch current user, using cached info:', error);
            dispatch({ type: 'INITIALIZE_STATE' });
          }
        }
      } else {
        dispatch({ type: 'INITIALIZE_STATE' });
      }
    } catch (error) {
      console.error('Failed to initialize app:', error);
      dispatch({ type: 'LOGOUT' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const login = React.useCallback((user: User, token: string) => {
    authService.setUserInfo(user);
    authService.setToken(token);
    dispatch({ type: 'SET_USER', payload: user });
    dispatch({ type: 'SET_TOKEN', payload: token });
    dispatch({ type: 'SET_AUTHENTICATED', payload: true });
  }, [dispatch]);

  const logout = React.useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await authService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
      // 清除所有本地存储
      authService.clearAuth();
      // 跳转到登录页
      window.location.href = '/auth/login';
    }
  }, [dispatch]);

  const toggleSidebar = React.useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  }, [dispatch]);

  const setSidebarCollapsed = React.useCallback((collapsed: boolean) => {
    dispatch({ type: 'SET_SIDEBAR_COLLAPSED', payload: collapsed });
  }, [dispatch]);

  const updateUser = React.useCallback((user: User) => {
    authService.setUserInfo(user);
    dispatch({ type: 'SET_USER', payload: user });
  }, [dispatch]);

  const checkAuthStatus = React.useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      updateUser(currentUser);
    } catch (error) {
      console.error('Auth check failed:', error);
      await logout();
    }
  }, [updateUser, logout]);

  const contextValue: AppContextType = React.useMemo(() => ({
    state,
    dispatch,
    login,
    logout,
    toggleSidebar,
    setSidebarCollapsed,
    updateUser,
    checkAuthStatus,
  }), [state, login, logout, toggleSidebar, setSidebarCollapsed, updateUser, checkAuthStatus]);

  return (
    <AppContext.Provider value={contextValue}>
      <NotificationProvider 
        isAuthenticated={state.isAuthenticated} 
        isLoading={state.loading}
      >
        {children}
      </NotificationProvider>
    </AppContext.Provider>
  );
};

// Hook for using context
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;